/**
 * CSV Parser Utility
 * 
 * Parses CSV content for bulk user operations.
 * Simple split-based parsing (does not support quoted values with commas).
 */

// Basic email regex for client-side validation
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Parsed user row with validation status
 */
export interface ParsedUser {
  rowNumber: number;
  email: string;
  name?: string;
  isValid: boolean;
  error?: string;
}

/**
 * Result of CSV parsing
 */
export interface CsvParseResult {
  users: ParsedUser[];
  validCount: number;
  invalidCount: number;
  hasHeader: boolean;
}

/**
 * Validates an email address format
 */
function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email);
}

/**
 * Parses CSV content for user bulk upload.
 * 
 * Supported formats:
 * - Single column (email only)
 * - Two columns (email, name)
 * - With or without header row
 * 
 * Limitations:
 * - Does not support quoted values with commas
 * - Names containing commas will be truncated
 * 
 * @param csvContent - Raw CSV string content
 * @returns Parsed users with validation status
 */
export function parseUsersCsv(csvContent: string): CsvParseResult {
  const lines = csvContent
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(line => line.length > 0);

  if (lines.length === 0) {
    return {
      users: [],
      validCount: 0,
      invalidCount: 0,
      hasHeader: false,
    };
  }

  // Detect if first row is a header by checking if first cell is exactly "email"
  // This avoids false positives like "email@domain.com" being treated as a header
  const firstLineParts = lines[0].split(",").map(part => part.trim().toLowerCase());
  const firstCell = firstLineParts[0];
  const hasHeader = firstCell === "email";
  
  // Skip header row if detected
  const dataLines = hasHeader ? lines.slice(1) : lines;
  
  // Track emails for duplicate detection
  const seenEmails = new Set<string>();
  
  const users: ParsedUser[] = dataLines.map((line, index) => {
    const rowNumber = hasHeader ? index + 2 : index + 1; // 1-based, accounting for header
    const parts = line.split(",").map(part => part.trim());
    
    const email = parts[0] || "";
    const name = parts[1] || undefined;
    
    // Validation
    let isValid = true;
    let error: string | undefined;
    
    if (!email) {
      isValid = false;
      error = "Email is required";
    } else if (!isValidEmail(email)) {
      isValid = false;
      error = "Invalid email format";
    } else if (seenEmails.has(email.toLowerCase())) {
      isValid = false;
      error = "Duplicate email in batch";
    }
    
    // Track email for duplicate detection
    if (email) {
      seenEmails.add(email.toLowerCase());
    }
    
    return {
      rowNumber,
      email,
      name: name || undefined,
      isValid,
      error,
    };
  });

  const validCount = users.filter(u => u.isValid).length;
  const invalidCount = users.filter(u => !u.isValid).length;

  return {
    users,
    validCount,
    invalidCount,
    hasHeader,
  };
}

/**
 * Reads a File object and returns its text content
 */
export async function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsText(file);
  });
}
